import { useEffect, useState } from "react";
import api, { downloadBlob, todayStr, fmtDateTime } from "../lib/api";
import Topbar from "../components/Topbar";
import EmptyState from "../components/EmptyState";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Upload, FileText, ExternalLink, BarChart3, Download, X, CheckCircle2, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROLE_OPTIONS = ["supplier", "branch_manager", "treasury", "legal", "corporate_admin"];

export default function AdminPrivacyNotice() {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsSearch, setStatsSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const h = await api.get("/privacy-notice/history");
      setHistory(h.data || []);
      const cur = (h.data || []).find((n) => n.is_current) || null;
      setCurrent(cur);
      if (cur) loadStats(cur.id);
      else setStats(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (id) => {
    try {
      const r = await api.get(`/privacy-notice/${id}/acceptance-stats`);
      setStats(r.data);
    } catch { setStats(null); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pdfUrl = (n) => `${BACKEND_URL}${n.pdf_url}`;

  const exportAcceptances = async () => {
    try {
      const v = stats?.version_number;
      const res = await api.get(`/privacy-notice/acceptances/export${v ? `?version_number=${v}` : ""}`, { responseType: "blob" });
      downloadBlob(res.data, `aceptaciones_aviso_${todayStr()}.xlsx`);
      toast.success(t("export.success"));
    } catch {
      toast.error(t("export.error"));
    }
  };

  const pct = stats && stats.total_users_required > 0
    ? Math.round((stats.accepted_count / stats.total_users_required) * 100) : 0;
  const pendingFiltered = (stats?.pending_users || []).filter((u) =>
    !statsSearch || `${u.email} ${u.supplier_business_name || ""}`.toLowerCase().includes(statsSearch.toLowerCase()));

  return (
    <>
      <Topbar title={t("privacyAdmin.title")} subtitle={t("privacyAdmin.subtitle")} />
      <div className="p-6 space-y-6 pb-28" data-testid="privacy-admin-page">

        {/* Versión actual */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-base text-[#1A1A1A]">{t("privacyAdmin.currentSection")}</h2>
            <button data-testid="privacy-upload-open-btn" onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold">
              <Upload className="w-4 h-4" /> {t("privacyAdmin.uploadBtn")}
            </button>
          </div>
          {loading ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : current ? (
            <div data-testid="privacy-current-card" className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#FFFBE6] border border-[#FFDF00] flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1A1A1A]">{current.title}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {t("privacyAdmin.versionLine", { n: current.version_number, date: current.effective_date })}
                </div>
                <div className="text-xs text-slate-400 mt-1">{t("privacyAdmin.appliesTo")}: {(current.applies_to_roles || []).map((r) => t(`roles.${r}`, { defaultValue: r })).join(", ")}</div>
                <a href={pdfUrl(current)} target="_blank" rel="noopener noreferrer" data-testid="privacy-current-view-pdf"
                  className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#1A1A1A] hover:underline">
                  <ExternalLink className="w-4 h-4" /> {t("privacyAdmin.viewPdf")}
                </a>
              </div>
            </div>
          ) : (
            <EmptyState title={t("privacyAdmin.emptyTitle")} message={t("privacyAdmin.emptyMsg")} />
          )}
        </section>

        {/* Estadísticas de aceptación */}
        {current && stats && (
          <section data-testid="privacy-stats-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-base text-[#1A1A1A]">{t("privacyAdmin.statsSection")}</h2>
              <button data-testid="privacy-export-btn" onClick={exportAcceptances}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-sm font-semibold">
                <Download className="w-4 h-4" /> {t("export.button")}
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /><span data-testid="privacy-accepted-count" className="font-bold text-lg">{stats.accepted_count}</span><span className="text-xs text-slate-500">{t("privacyAdmin.accepted")}</span></div>
                <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-amber-500" /><span data-testid="privacy-pending-count" className="font-bold text-lg">{stats.pending_count}</span><span className="text-xs text-slate-500">{t("privacyAdmin.pending")}</span></div>
                <div className="text-xs text-slate-400">{t("privacyAdmin.totalRequired", { n: stats.total_users_required })}</div>
              </div>
              <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              {stats.pending_count > 0 && (
                <div>
                  <input data-testid="privacy-pending-search" type="text" value={statsSearch}
                    onChange={(e) => setStatsSearch(e.target.value)} placeholder={t("privacyAdmin.searchPending")}
                    className="w-full max-w-xs px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                  <div className="max-h-60 overflow-y-auto space-y-1.5">
                    {pendingFiltered.map((u) => (
                      <div key={u.user_id} data-testid={`privacy-pending-row-${u.user_id}`}
                        className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
                        <span className="font-medium text-[#1A1A1A] truncate">{u.supplier_business_name}</span>
                        <span className="text-xs text-slate-500 truncate ml-3">{u.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Histórico */}
        {history.length > 0 && (
          <section>
            <h2 className="font-display font-bold text-base text-[#1A1A1A] mb-3">{t("privacyAdmin.historySection")}</h2>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="text-left px-4 py-2.5">{t("privacyAdmin.colVersion")}</th>
                    <th className="text-left px-4 py-2.5">{t("privacyAdmin.colTitle")}</th>
                    <th className="text-left px-4 py-2.5">{t("privacyAdmin.colEffective")}</th>
                    <th className="text-left px-4 py-2.5">{t("privacyAdmin.colPublished")}</th>
                    <th className="text-left px-4 py-2.5">{t("privacyAdmin.colStatus")}</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((n) => (
                    <tr key={n.id} data-testid={`privacy-history-row-${n.version_number}`} className="border-t border-slate-100">
                      <td className="px-4 py-2.5 font-bold">v{n.version_number}</td>
                      <td className="px-4 py-2.5">{n.title}</td>
                      <td className="px-4 py-2.5 text-slate-500">{n.effective_date}</td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDateTime(n.created_at)}</td>
                      <td className="px-4 py-2.5">
                        {n.is_current
                          ? <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">{t("privacyAdmin.statusCurrent")}</span>
                          : <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{t("privacyAdmin.statusSuperseded")}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <a href={pdfUrl(n)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1A1A1A] hover:underline text-xs font-semibold">
                          <FileText className="w-3.5 h-3.5" /> PDF
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
    </>
  );
}

const UploadModal = ({ onClose, onDone }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("Aviso de Privacidad");
  const [effectiveDate, setEffectiveDate] = useState(todayStr());
  const [roles, setRoles] = useState(["supplier"]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const toggleRole = (r) => setRoles((cur) => cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]);

  const submit = async () => {
    if (!file) { toast.error(t("privacyAdmin.fileRequired")); return; }
    if (!title.trim()) { toast.error(t("privacyAdmin.titleRequired")); return; }
    if (roles.length === 0) { toast.error(t("privacyAdmin.rolesRequired")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      fd.append("title", title.trim());
      fd.append("effective_date", effectiveDate);
      fd.append("applies_to_roles", JSON.stringify(roles));
      if (notes.trim()) fd.append("notes", notes.trim());
      await api.post("/admin/privacy-notice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("privacyAdmin.publishedToast"));
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="privacy-upload-modal"
        className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-base text-[#1A1A1A]">{t("privacyAdmin.uploadTitle")}</div>
          <button data-testid="privacy-upload-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("privacyAdmin.pdfFile")} *</label>
            <input data-testid="privacy-upload-file" type="file" accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#FFDF00] file:font-semibold file:text-[#1A1A1A]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("privacyAdmin.titleField")} *</label>
            <input data-testid="privacy-upload-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("privacyAdmin.effectiveDate")} *</label>
            <input data-testid="privacy-upload-date" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("privacyAdmin.appliesTo")}</label>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((r) => (
                <button key={r} type="button" data-testid={`privacy-role-${r}`} onClick={() => toggleRole(r)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${roles.includes(r) ? "border-[#FFDF00] bg-[#FFFBE6] text-[#1A1A1A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {t(`roles.${r}`, { defaultValue: r })}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1.5 block">{t("privacyAdmin.notesField")}</label>
            <textarea data-testid="privacy-upload-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          {confirming ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              <p className="mb-3">{t("privacyAdmin.confirmText")}</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirming(false)} disabled={busy}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold">{t("common.cancel")}</button>
                <button data-testid="privacy-upload-confirm-btn" onClick={submit} disabled={busy}
                  className="btn-primary-chizy flex-1 py-2 rounded-lg text-sm disabled:opacity-50">
                  {busy ? t("common.saving") : t("privacyAdmin.confirmPublish")}
                </button>
              </div>
            </div>
          ) : (
            <button data-testid="privacy-upload-submit-btn" onClick={() => setConfirming(true)}
              className="btn-primary-chizy w-full py-2.5 rounded-xl text-sm">
              {t("privacyAdmin.publishBtn")}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};
